import { apiMunzen } from '@/app/api/apiMunzen/apiMunzen';
import type { FirstSteps } from '../types/firstSteps.interface';
import type { UpdatePasswordForm, UpdatePasswordRequest } from '../types/updatePassword.interface';
import type {
  UpdatePersonalInfoForm,
  UpdatePersonalInfoRequest,
} from '../types/updatePersonalInfo.interface';
import type {
  ChangeRegisteredEmailRequest,
  ResendEmailVerificationTokenRequest,
  ValidateEmailVerificationTokenRequest,
} from '../types/updateRegisteredEmail.interface';
import type { User } from '../types/user.interface';

/**
 * @author: Omar Sánchez Martinez
 * @since: 26/05/2025
 * @description: Verifica si los pasos iniciales del usuario han sido completados
 */
export const checkFirstStepsCompletedService = async (): Promise<FirstSteps> => {
  const response = await apiMunzen.get('/users/check-first-steps-completed');
  return response.data.data;
};

/**
 * @author: Omar Sánchez Martinez
 * @since: 26/05/2025
 * @description: Obtiene el total de créditos restantes del usuario
 */
export const fetchUserTotalRemainingCreditsService = async (): Promise<number> => {
  const response = await apiMunzen.get('/users/get-total-remaining-credits');
  return response.data.data.user_total_remaining_credits;
};

/**
 * @author: Omar Sánchez Martinez
 * @since: 30/05/2025
 * @description: Recupera la contraseña del usuario
 */
export const recoverPasswordService = async (email: string): Promise<boolean> => {
  const response = await apiMunzen.post('/users/reset-password', { email });
  return response.data.data.success;
};

/**
 * @author: Omar Sánchez Martinez
 * @since: 01/06/2025
 * @description: Verifica si el email es único
 */
export const uniqueUserEmailService = async (
  email: string,
  signal?: AbortSignal,
): Promise<boolean> => {
  const response = await apiMunzen.get(`/users/unique-email/${email}`, { signal });
  return response.data.data.success;
};

/**
 * @author: Omar Sánchez Martinez
 * @since: 21/06/2025
 * @description: Verifica si el email existe
 */
export const existingUserEmailService = async (email: string): Promise<boolean> => {
  const response = await apiMunzen.get(`/users/unique-email/${email}`);
  return !response.data.data.success;
};

/**
 * @author: Omar Sánchez Martinez
 * @since: 01/06/2025
 * @description: Actualiza la contraseña del usuario
 */
export const updatePasswordService = async (
  updatePasswordForm: UpdatePasswordForm,
): Promise<boolean> => {
  const requestData: UpdatePasswordRequest = {
    password: updatePasswordForm.password,
    new_password: updatePasswordForm.new_password,
  };

  const response = await apiMunzen.post('/users/change-password', requestData);
  return response.data.data.success;
};

/**
 * @author: Omar Sánchez Martinez
 * @since: 23/06/2025
 * @description: Cambia el email registrado del usuario
 */
export const changeRegisteredEmailService = async (email: string): Promise<boolean> => {
  const requestData: ChangeRegisteredEmailRequest = { email };
  const response = await apiMunzen.post('/users/change-personal-email', requestData);
  return response.data.data.success;
};

/**
 * @author: Omar Sánchez Martinez
 * @since: 23/06/2025
 * @description: Reenvía el token de verificación de email
 */
export const resendEmailVerificationTokenService = async (email: string): Promise<boolean> => {
  const requestData: ResendEmailVerificationTokenRequest = { email };
  const response = await apiMunzen.post('/users/resend-token-personal-email', requestData);
  return response.data.data.success;
};

/**
 * @author: Omar Sánchez Martinez
 * @since: 23/06/2025
 * @description: Valida el token de verificación de email
 * @throws {ApiError} Si el token es inválido o hay un error del servidor
 */
export const validateEmailVerificationTokenService = async (
  token: string,
  email: string,
): Promise<boolean> => {
  const requestData: ValidateEmailVerificationTokenRequest = {
    email_verification_token: token,
    email,
  };
  const response = await apiMunzen.post('/users/validate-token-personal-email', requestData);
  return response.data.data.success;
};

/**
 * @author: Omar Sánchez Martinez
 * @since: 23/06/2025
 * @description: Actualiza la información personal del usuario
 */
export const updatePersonalInfoService = async (
  updatePersonalInfoForm: UpdatePersonalInfoForm,
): Promise<boolean> => {
  const requestData: UpdatePersonalInfoRequest = {
    name: updatePersonalInfoForm.name,
    phone: updatePersonalInfoForm.phone,
  };

  const response = await apiMunzen.post('/users/change-personal-information', requestData);
  return response.data.data.success;
};

/**
 * @author: Omar Sánchez Martinez
 * @since: 10/02/2026
 * @description: Obtiene los datos actuales del usuario autenticado con todas sus empresas
 */
export const getAuthenticatedUserDataService = async (): Promise<User> => {
  const response = await apiMunzen.get('/users/get-authenticated-user-data');
  return response.data.data;
};
